
/* ============================================
   TESTIMONIAL.JSX
   📁 src/components/Buyer/Home/Testimonial.jsx
   ✅ tst- prefix CSS classes
   ✅ Brand kit: #ffffff / #000000
   ✅ Square cards: Buyer image + Seller image with role labels
   ✅ Right side: review text + client info + property card
   ✅ Stars, progress bar, dots all active
   ✅ Smooth fade animation on next/prev
   ============================================ */

import React, { useContext, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { FaMapMarkerAlt, FaBed, FaBath, FaRulerCombined } from 'react-icons/fa';
import '../../../assets/css/Buyer/Home/testimonial.css';
import axios from 'axios';
import AppContext from 'context/AppContext';

const Testimonial = () => {
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fading, setFading] = useState(false);

  const fetchTestimonials = () => {
    axios
      .get(`${baseUrl}/buyer/testimonial/getTestimonials`)
      .then((response) => {
        console.log(response.data);
        setTestimonials(response.data.testimonial || []);
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const total = testimonials.length;
  if (total === 0) return null;

  const current = testimonials[currentIndex] || {};

  // ── Animated navigation ─────────────────────────────────
  const goTo = (idx) => {
    if (fading || idx === currentIndex) return;
    setFading(true);
    setTimeout(() => {
      setCurrentIndex(idx);
      setFading(false);
    }, 260);
  };

  const goPrev = () => goTo((currentIndex - 1 + total) % total);
  const goNext = () => goTo((currentIndex + 1) % total);

  // Progress bar fill %
  const progressPct = total > 1 ? (currentIndex / (total - 1)) * 100 : 0;

  // ── RENDER ──────────────────────────────────────────────
  return (
    <section className="tst-section">
      <div className="tst-container">

        {/* Heading */}
        <h2 className="tst-heading">What Our Clients Say</h2>

        <div className="tst-grid">

          {/* ════════════════════════
              LEFT COLUMN
              ════════════════════════ */}
          <div className="tst-left">

            {/* ── Two square profile cards: Buyer + Seller ── */}
            <div className="tst-cards-row">

              {/* Buyer card */}
              <div className="tst-profile-card tst-profile-card--active">
                <div
                  className="tst-profile-img"
                  style={{
                    backgroundImage: `url(${baseUrlForFiles}${current?.exbuyer?.profileImage || ''})`,
                  }}
                />
                <span className="tst-profile-label">{current?.exbuyer?.firstName}  </span>
              </div>

              {/* Seller card — linked property's seller image */}
              <div className="tst-profile-card">
                <div
                  className="tst-profile-img"
                  style={{
                    backgroundImage: `url(${
                      current?.exproperty?.exseller?.profileImage
                        ? baseUrlForFiles + current.exproperty.exseller.profileImage
                        : 'https://via.placeholder.com/96?text=Seller'
                    })`,
                  }}
                />
                <span className="tst-profile-label">
                  {current?.exproperty?.exseller?.firstName} 
                </span>
              </div>
            </div>

            {/* ── Star Rating ── */}
            <div className="tst-stars-row">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="tst-star"
                  fill={i < (current?.rating || 0) ? '#FF6B35' : '#E0E0E0'}
                  color={i < (current?.rating || 0) ? '#FF6B35' : '#E0E0E0'}
                />
              ))}
              <span className="tst-rating-val">{current?.rating || 0}.0</span>
            </div>

            {/* ── Dot indicators ── */}
            <div className="tst-dots-row">
              {testimonials.map((_, i) => (
                <span
                  key={i}
                  className={`tst-dot ${i === currentIndex ? 'tst-dot--active' : ''}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          </div>

          {/* ════════════════════════
              CENTER — Quote icon
              ════════════════════════ */}
          <div className="tst-center">
            <div className="tst-quote-icon">❝</div>
          </div>

          {/* ════════════════════════
              RIGHT COLUMN
              ════════════════════════ */}
          <div className="tst-right">

            {/* Review text with fade animation */}
            <div className={`tst-review-wrap ${fading ? 'tst-review-wrap--fading' : ''}`}>
              <p className="tst-review-text">{current?.comment || ''}</p>
            </div>

            {/* Progress divider */}
            <div className="tst-divider">
              <div
                className="tst-divider-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Client info + Navigation buttons */}
            <div className="tst-bottom">
              <div className="tst-client-info">
                <div
                  className="tst-client-avatar"
                  style={{
                    backgroundImage: `url(${baseUrlForFiles}${current?.exproperty?.exseller?.profileImage || ''})`,
                  }}
                />
                <div>
                  <h4 className="tst-client-name">
                    {current?.exproperty?.exseller?.firstName} {current?.exproperty?.exseller?.lastName} 
                  </h4>
                  <p className="tst-client-location">{current?.exproperty?.exseller?.location}</p>
                </div>
              </div>

              <div className="tst-nav-wrap">
                <button className="tst-nav-btn" onClick={goPrev} aria-label="Previous">
                  <ChevronLeft size={18} />
                </button>
                <button className="tst-nav-btn" onClick={goNext} aria-label="Next">
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* ── Property Card ── */}
            {current?.exproperty && (
              <div className="tst-prop-card">
                <div
                  className="tst-prop-img"
                  style={{
                    backgroundImage: `url(${baseUrlForFiles}${
                      current.exproperty?.expropertyimages?.[0]?.images || ''
                    })`,
                  }}
                />
                <div className="tst-prop-info">
                  <h4 className="tst-prop-title">
                    {current.exproperty?.property_title}
                  </h4>
                  <p className="tst-prop-location">
                    <FaMapMarkerAlt />
                    <span>{current.exproperty?.location}</span>
                  </p>
                  <div className="tst-prop-specs">
                    {current.exproperty?.bedrooms > 0 && (
                      <span className="tst-prop-spec">
                        <FaBed /> {current.exproperty.bedrooms} Beds
                      </span>
                    )}
                    {current.exproperty?.bathrooms > 0 && (
                      <span className="tst-prop-spec">
                        <FaBath /> {current.exproperty.bathrooms} Baths
                      </span>
                    )}
                    {current.exproperty?.size && (
                      <span className="tst-prop-spec">
                        <FaRulerCombined /> {current.exproperty.size}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonial;
